import { CLUB_SENDER_ID, courtOfSender, type NameSource } from '../../engine/messaging';
import { DEFAULT_LOCALE, translate, type LocaleContextValue } from '../../i18n';

// Shared bits for the messaging screens (mobile inbox/thread and the
// desktop club view): who is called what, and the club's own display name.

/**
 * The translator, defaulted to the SOURCE catalog (#310).
 *
 * This helper is not a component and cannot call `useT()`, and it is shared
 * with `components/messages/*`, which is not on the catalog yet. An optional
 * parameter keeps that caller working EXACTLY as before — Norwegian, from the
 * catalog rather than from a literal typed into this file — while the migrated
 * mobile screens pass their own `t` and follow the reader's language. The
 * default becomes dead the day the desktop surface migrates.
 */
type Translate = LocaleContextValue['t'];
const sourceCatalog: Translate = (key, vars) => translate(DEFAULT_LOCALE, key, vars);

/** Name lookup over the player list, with the club as a named sender. */
export function nameSourceFor(
  effectivePlayerDB: any[] = [],
  clubInfo: any = null,
  t: Translate = sourceCatalog,
): NameSource {
  const byId = new Map<string, string>();
  for (const p of effectivePlayerDB || []) {
    if (p?.id != null) byId.set(String(p.id), String(p.name || '').trim());
  }
  const clubName = String(clubInfo?.name || '').trim() || t('mobile.messaging.clubFallback');
  return {
    nameOf: (id: string) => {
      if (id === CLUB_SENDER_ID) return clubName;
      const court = courtOfSender(id);
      if (court != null) return t('mobile.messaging.courtPad', { court: String(court) });
      return byId.get(String(id)) || '';
    },
    clubName,
  };
}

/** Bubble colours: mine lime, the club's dark, everyone else's white. */
export function bubbleStyle(senderId: string, meId: string): React.CSSProperties {
  if (senderId === meId) return { background: '#c2f02f', color: '#0f1611' };
  if (senderId === CLUB_SENDER_ID) return { background: '#0f1611', color: '#fff' };
  return { background: '#fff', color: '#0f1611', border: '1px solid #e7eae3' };
}
